import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Drawer } from "antd";
import { ArrowLeftOutlined, CloseOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import AuthService from "../../base/services/authentication.service";
import { getStoredBillingStatus, isBillingOnly, isHeldByGate } from "../Billing/billingStore";
import { HELP_VIDEOS, TICKETS_ROLES } from "./helpCenterConfig";
import { getPlayableVideos } from "./helpVideos";
import { filterByRoles, getVisibleArticles } from "./guideHelpers";
import { USER_GUIDE } from "./guide/articles";
import { PRODUCT_UPDATES } from "./productUpdates";
import { getPageArticles } from "./pageHelp";
import { HELP_LINK_VIEWS, isHelpShortcut, readHelpLink, removeHelpParam } from "./helpTriggers";
import { fetchRecentTickets } from "./helpCenterApi";
import HelpHome from "./HelpHome";
import VideoLibrary from "./VideoLibrary";
import WhatsNew from "./WhatsNew";
import { UserGuideArticle, UserGuideList } from "./UserGuide";
import { AskAssistantView, AskComposer, useHelpAssistant } from "./AskAssistant";

import "./HelpCenter.scss";

// A link from outside the app (an email, a ticket reply) opens "/?help=…",
// and "/" sends each role on to its home page without the query string. This
// module is evaluated with the app bundle, before anything renders, so the
// link is read here and the first Help Center that mounts opens it.
let linkFromPageLoad = readHelpLink(window.location.search);

const VIEW_TITLES = {
   videos: "Video Library",
   guide: "User Guide",
   article: "User Guide",
   "whats-new": "What's new",
   ask: "Ask AI"
};

const isAuthorized = (roles) => AuthService.isAuthorized(roles);

/**
 * Help button in the top bar plus the Help Center drawer it opens. Lives in
 * SubHeader, so it is on every signed-in page. Closes itself on navigation
 * because the layout (and with it this component) stays mounted across routes.
 * Also opens with the "?" key and with a ?help=<article or view> link.
 */
const HelpCenter = () => {
   const location = useLocation();
   const navigate = useNavigate();
   const [open, setOpen] = useState(false);
   // Views opened on top of the home menu, e.g. [{ view: "guide" }, { view: "article", id }].
   const [stack, setStack] = useState([]);
   const current = stack[stack.length - 1];
   const searchRef = useRef(null);
   const focusSearch = useRef(false);
   const assistant = useHelpAssistant();
   const [recentTickets, setRecentTickets] = useState(null);

   const videos = useMemo(() => getPlayableVideos(HELP_VIDEOS), []);
   // Re-read on open: roles change when the user switches organization.
   // eslint-disable-next-line react-hooks/exhaustive-deps
   const articles = useMemo(() => getVisibleArticles(USER_GUIDE, isAuthorized), [open]);
   // eslint-disable-next-line react-hooks/exhaustive-deps
   const updates = useMemo(() => filterByRoles(PRODUCT_UPDATES, isAuthorized), [open]);
   const pageArticles = useMemo(() => getPageArticles(location.pathname, articles), [
      location.pathname,
      articles
   ]);
   const canOpenTickets = isAuthorized(TICKETS_ROLES);
   const organizationId = AuthService.getOrganizationId();
   const article = current?.view === "article" && articles.find((item) => item.id === current.id);

   const close = () => setOpen(false);
   const push = (entry) => setStack((views) => [...views, entry]);
   const back = () => setStack((views) => views.slice(0, -1));
   const openArticle = (id) => push({ view: "article", id });

   const openWhatsNew = () => push({ view: "whats-new" });

   const askAi = (question) => {
      push({ view: "ask" });
      if (question) assistant.ask(question);
   };

   // The views a ?help= link opens: an article, a view, or the home menu.
   const linkTarget = (target) => {
      if (HELP_LINK_VIEWS.includes(target)) return [{ view: target }];
      if (articles.some((item) => item.id === target)) return [{ view: "article", id: target }];
      return [];
   };

   useEffect(() => {
      setOpen(false);
   }, [location.pathname]);

   // Declared after the effect above, so a link to another page still opens.
   useEffect(() => {
      const fromUrl = readHelpLink(location.search);
      const target = fromUrl || linkFromPageLoad;
      linkFromPageLoad = null;
      if (!target) return;

      if (fromUrl) {
         navigate(
            {
               pathname: location.pathname,
               search: removeHelpParam(location.search),
               hash: location.hash
            },
            { replace: true }
         );
      }
      setStack(linkTarget(target));
      setOpen(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [location.search]);

   useEffect(() => {
      const handleKeyDown = (event) => {
         if (!isHelpShortcut(event)) return;
         event.preventDefault();
         focusSearch.current = true;
         setOpen(true);
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
   }, []);

   // Fresh on every open. An organization held at billing has every call
   // refused until it pays, so nothing is asked for then.
   useEffect(() => {
      if (!open || !organizationId || !canOpenTickets) return undefined;
      if (isBillingOnly(getStoredBillingStatus(organizationId)) || isHeldByGate(organizationId)) {
         return undefined;
      }
      let isCurrent = true;

      fetchRecentTickets()
         .then((result) => isCurrent && setRecentTickets(result))
         .catch(() => isCurrent && setRecentTickets(null));

      return () => {
         isCurrent = false;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [open]);

   const title = current ? (
      <div className="help-center__title">
         <Button
            type="text"
            size="small"
            icon={<ArrowLeftOutlined />}
            onClick={back}
            aria-label="Back"
         />
         <span>{VIEW_TITLES[current.view]}</span>
      </div>
   ) : (
      <div className="help-center__title help-center__title--home">
         <span>Help Center</span>
         <small>Learn the platform or get in touch with our team.</small>
      </div>
   );

   let content;
   if (current?.view === "videos") {
      content = <VideoLibrary videos={videos} />;
   } else if (current?.view === "guide") {
      content = <UserGuideList articles={articles} onOpen={openArticle} />;
   } else if (current?.view === "whats-new") {
      content = (
         <WhatsNew
            updates={updates}
            articles={articles}
            onOpenArticle={openArticle}
            onClose={close}
         />
      );
   } else if (current?.view === "ask") {
      content = (
         <AskAssistantView
            assistant={assistant}
            articles={articles}
            onOpenArticle={openArticle}
            onClose={close}
            canOpenTickets={canOpenTickets}
         />
      );
   } else if (article) {
      content = (
         <UserGuideArticle
            article={article}
            articles={articles}
            onOpen={openArticle}
            onClose={close}
            canOpenTickets={canOpenTickets}
         />
      );
   }

   return (
      <>
         <button
            type="button"
            className="help-center-trigger"
            onClick={() => setOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={open}
         >
            <QuestionCircleOutlined />
            <span className="help-center-trigger__label">Help</span>
         </button>

         <Drawer
            open={open}
            onClose={close}
            placement="right"
            width={440}
            title={title}
            closable={false}
            extra={
               <Button
                  type="text"
                  icon={<CloseOutlined />}
                  onClick={close}
                  aria-label="Close Help Center"
               />
            }
            footer={current?.view === "ask" ? <AskComposer assistant={assistant} /> : null}
            destroyOnClose
            afterOpenChange={(visible) => {
               if (visible && focusSearch.current) searchRef.current?.focus();
               if (!visible) setStack([]);
               focusSearch.current = false;
            }}
            rootClassName="help-center"
         >
            {content || (
               <HelpHome
                  searchRef={searchRef}
                  articles={articles}
                  videos={videos}
                  pageArticles={pageArticles}
                  updateCount={updates.length}
                  recentTickets={recentTickets}
                  canOpenTickets={canOpenTickets}
                  onPush={push}
                  onOpenArticle={openArticle}
                  onOpenWhatsNew={openWhatsNew}
                  onAsk={askAi}
                  onClose={close}
               />
            )}
         </Drawer>
      </>
   );
};

export default HelpCenter;
